import { useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const SESSION_STORAGE_KEY = "ho-active-session-id";

/**
 * Global watcher that listens for live_sessions status changes for the current user.
 * Shows a toast + browser notification when any active session ends or errors,
 * even when the user is on a completely different page.
 *
 * Mount this once in a top-level layout component (e.g. App or ProtectedRoute).
 */
export function useActiveSessionWatcher() {
  const { user } = useAuth();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("global-session-watcher")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "live_sessions",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const updated = payload.new as {
            id: string;
            status: string;
            title: string | null;
          };

          if (updated.status === "ended" || updated.status === "error") {
            const title = updated.title || "Agent Session";
            const isError = updated.status === "error";

            // In-app toast
            if (isError) {
              toast.error(`Session failed: ${title}`);
            } else {
              toast.success(`Session complete: ${title}`, {
                action: {
                  label: "View",
                  onClick: () => {
                    localStorage.setItem(SESSION_STORAGE_KEY, updated.id);
                    window.location.href = "/sessions";
                  },
                },
              });
            }

            // Browser notification (when tab is hidden / app is backgrounded)
            if (
              document.visibilityState !== "visible" &&
              "Notification" in window &&
              Notification.permission === "granted"
            ) {
              try {
                new Notification(
                  isError ? "Session Error" : "Session Complete",
                  {
                    body: title,
                    icon: "/pwa-192x192.png",
                    tag: `session-${updated.id}`,
                    data: { url: "/sessions" },
                  }
                );
              } catch {
                // Notification constructor may throw in some contexts
              }
            }
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [user]);
}
